import { useSuspenseQuery } from "@apollo/client/react";
import { parseISO } from "date-fns";
import { Check, Download, ExternalLink, MoreHorizontal, Pencil, UploadCloud } from "lucide-react";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useShowValidationErrors } from "~/common/hooks";
import type { MutationRefetchQueries } from "~/common/types";
import { DetailLoading } from "~/components/loading";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
} from "~/components/ui";
import { DataImportProjectsModelProvider, useDipLifecycle } from "~/features/dataImportProjects/context";
import {
  GetDataSourceDetailDocument,
  type GetDataSourceDetailQuery,
  type GetDataSourceDetailQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useDataSourceActions } from "~/features/dataImportProjects/hooks/useDataSourceActions";
import { useDataSourceActivation } from "~/features/dataImportProjects/hooks/useDataSourceActivation";
import { useDataSourceJsonDownload } from "~/features/dataImportProjects/hooks/useDataSourceJsonDownload";
import { useDataSourceSheetIdEdit } from "~/features/dataImportProjects/hooks/useDataSourceSheetIdEdit";
import { useDipResourceMutationGuard } from "~/features/dataImportProjects/hooks/useDipResourceMutationGuard";
import {
  type DataSourceSheetIdEditFormData,
  zDataSourceSheetIdEditFormDataKeys,
} from "~/features/dataImportProjects/types";
import { DataSourceUtil } from "~/features/dataImportProjects/utils/dataSource";
import { useTenant } from "~/features/tenant/context";
import { cn } from "~/lib/utils";
import { rootRoute as R } from "~/routes";
import { DATA_SOURCE_EDIT_FORM_ID, DataSourceEditForm } from "./DataSourceEditForm";

import { DataSourceJsonEditDialog } from "./DataSourceJsonEditDialog";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";

type DataSource = GetDataSourceDetailQuery["dataSources"][number];

type DataSourceDetailPanelProps = {
  importProjectId: string;
  dataSourceId: string;
  mode?: "peek" | "page";
};

type MetadataRowProps = {
  label: string;
  children: ReactNode;
};

function MetadataRow({ label, children }: MetadataRowProps) {
  return (
    <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
      <dt className="text-12-regular text-fg-3">{label}</dt>
      <dd className="min-w-0 text-14-regular text-fg-1">{children}</dd>
    </div>
  );
}

function formatDate(t: ReturnType<typeof useTranslation>["t"], iso: string | null | undefined) {
  return iso ? t("date.short", { date: parseISO(iso) }) : "-";
}

function dataSourceTypeLabel(t: ReturnType<typeof useTranslation>["t"], dataSource: DataSource) {
  if (dataSource.dataSourceGSheets) {
    return t("model.dataSource.typeGsheets");
  }
  if (dataSource.dataSourceJson) {
    return t("model.dataSource.typeJson");
  }
  return "-";
}

function DataSourceSheetIdEditDialog({
  open,
  onOpenChange,
  dataSourceId,
  sheetId,
  refetchQueriesOnEdit,
  readOnly,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dataSourceId: string;
  sheetId: number;
  refetchQueriesOnEdit: MutationRefetchQueries;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const form = useForm<DataSourceSheetIdEditFormData>({
    defaultValues: { sheetId: String(sheetId) },
  });
  const showValidationErrors = useShowValidationErrors(form.setError, zDataSourceSheetIdEditFormDataKeys);
  const { submit, processing } = useDataSourceSheetIdEdit({
    dataSourceId,
    refetchQueriesOnEdit,
    onSubmit: () => onOpenChange(false),
    onError: (error) => showValidationErrors(error.fields),
  });

  return (
    <Dialog open={open && !readOnly} onOpenChange={(nextOpen) => !processing && onOpenChange(nextOpen)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("ui.edit")} {t("model.dataSource.modelName")} {t("model.dataSource.sheetId")}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            id="data-source-sheet-id-edit-form"
            onSubmit={form.handleSubmit((values) => {
              if (readOnly) return;
              return submit(values);
            })}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="sheetId"
              rules={{
                required: t("ui.required"),
                validate: (value) => {
                  const numValue = Number(value);
                  if (Number.isNaN(numValue)) {
                    return t("model.dataSource.googleSheetIdValidation.invalidNumber");
                  }
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t("model.dataSource.sheetId")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  {fieldState.error?.message ? (
                    <p className="text-12-regular text-danger">{fieldState.error.message}</p>
                  ) : null}
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter>
          <Button type="button" variant="ghost" disabled={processing} onClick={() => onOpenChange(false)}>
            {t("ui.cancel")}
          </Button>
          <Button type="submit" form="data-source-sheet-id-edit-form" disabled={processing || readOnly}>
            {t("ui.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DataSourceEditBridgeDialog({
  open,
  onOpenChange,
  dataSourceId,
  refetchQueriesOnEdit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dataSourceId: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
}) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
            <div>
              <DialogTitle>
                {t("ui.edit")} {t("model.dataSource.modelName")}
              </DialogTitle>
              <p className="mt-1 text-12-regular text-fg-3">
                {t("feature.dataImportProjects.v2.temporaryLegacyBridge", {
                  defaultValue: "Temporary legacy component bridge",
                })}
              </p>
            </div>
            <Button type="submit" form={DATA_SOURCE_EDIT_FORM_ID} size="sm">
              {t("ui.save")}
            </Button>
          </div>
        </DialogHeader>
        <div className="min-w-[560px]">
          <Suspense fallback={<DetailLoading />}>
            <DataImportProjectsModelProvider>
              <DataSourceEditForm
                key={dataSourceId}
                dataSourceId={dataSourceId}
                onSubmit={() => onOpenChange(false)}
                refetchQueriesOnEdit={refetchQueriesOnEdit}
              />
            </DataImportProjectsModelProvider>
          </Suspense>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DataSourceSchemaSummary({ dataSource }: { dataSource: DataSource }) {
  const { t } = useTranslation();
  const schemas = dataSource.dataSourcePropertySchemaSet[0]?.propertySchemaSet.propertySchemaAssociations ?? [];

  if (schemas.length === 0) {
    return <p className="text-12-regular text-fg-3">-</p>;
  }

  return (
    <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
      <table className="w-full min-w-[680px] border-collapse text-12-regular">
        <thead className="bg-bg-subtle text-fg-2">
          <tr>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.common.name")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.propertySchema.propertyTypeId")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
              {t("model.common.description")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-center font-semibold">
              {t("model.propertySchema.required")}
            </th>
            <th className="border-b border-border-default px-3 py-2 text-center font-semibold">
              {t("model.propertySchema.unique")}
            </th>
          </tr>
        </thead>
        <tbody>
          {[...schemas]
            .sort((a, b) => a.position - b.position)
            .map((schema) => (
              <tr key={schema.id} className="border-t border-border-default first:border-t-0">
                <td className="px-3 py-2 text-fg-1">{schema.name}</td>
                <td className="px-3 py-2 text-fg-2">{schema.propertySchema.propertyTypeId}</td>
                <td className="px-3 py-2 text-fg-2">{schema.description || "-"}</td>
                <td className="px-3 py-2 text-center">
                  {schema.required ? (
                    <Badge tone="primary">
                      <Check className="size-3" />
                    </Badge>
                  ) : (
                    <span className="text-fg-3">-</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {schema.unique ? (
                    <Badge tone="primary">
                      <Check className="size-3" />
                    </Badge>
                  ) : (
                    <span className="text-fg-3">-</span>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function DataSourceInfoPanel({
  dataSource,
  onRefetch,
  readOnly,
  canExecute,
}: {
  dataSource: DataSource;
  onRefetch: () => void;
  readOnly: boolean;
  canExecute: () => boolean;
}) {
  const { t } = useTranslation();
  const { onApplySchemaClick, onClaspPushClick } = useDataSourceActions({ canExecute });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.15fr)]">
      <section className="space-y-4">
        <dl className="grid gap-3">
          <MetadataRow label={t("model.common.name")}>{dataSource.name}</MetadataRow>
          <MetadataRow label={t("model.dataSource.type")}>{dataSourceTypeLabel(t, dataSource)}</MetadataRow>
          {dataSource.description ? (
            <MetadataRow label={t("model.common.description")}>{dataSource.description}</MetadataRow>
          ) : null}
          <MetadataRow label={t("model.reference.modelNames")}>
            {dataSource.dataSourceReferences.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {dataSource.dataSourceReferences.map((dsf) => (
                  <Button key={dsf.reference.id} asChild variant="ghost" size="sm" className="h-auto p-0">
                    <Link to={R.references.detail.$buildPath({ params: { id: dsf.reference.id } })}>
                      {dsf.reference.title}
                    </Link>
                  </Button>
                ))}
              </div>
            ) : (
              "-"
            )}
          </MetadataRow>
          {dataSource.dataSourceJson?.s3Path ? (
            <MetadataRow label={t("model.dataSource.jsonFile")}>
              <code className="break-all rounded-[var(--radius-xs)] bg-bg-subtle px-1.5 py-0.5 text-12-regular">
                {dataSource.dataSourceJson.s3Path}
              </code>
            </MetadataRow>
          ) : null}
          {dataSource.dataSourceGSheets?.googleAppsScriptId ? (
            <MetadataRow label={t("model.dataSource.appsScript")}>
              <div className="space-y-2">
                <Button asChild variant="ghost" size="sm" className="h-auto p-0">
                  <a
                    href={DataSourceUtil.googleAppsScriptUrl(dataSource.dataSourceGSheets.googleAppsScriptId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("ui.open")} {t("model.dataSource.appsScript")}
                  </a>
                </Button>
                <div className="flex flex-wrap gap-2">
                  <DipReadOnlyTooltip
                    disabled={readOnly}
                    ariaLabel={t("feature.dataImportProjects.detail.dataSource.applySchema")}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={readOnly}
                      onClick={() => {
                        if (readOnly) return;
                        if (dataSource.dataSourceGSheets) {
                          onApplySchemaClick(dataSource, dataSource.dataSourceGSheets);
                          onRefetch();
                        }
                      }}
                    >
                      {t("feature.dataImportProjects.detail.dataSource.applySchema")}
                    </Button>
                  </DipReadOnlyTooltip>
                  <DipReadOnlyTooltip
                    disabled={readOnly}
                    ariaLabel={t("feature.dataImportProjects.detail.dataSource.push")}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={readOnly}
                      onClick={() => {
                        if (readOnly) return;
                        onClaspPushClick(dataSource);
                      }}
                    >
                      <UploadCloud className="size-3.5" />
                      {t("feature.dataImportProjects.detail.dataSource.push")}
                    </Button>
                  </DipReadOnlyTooltip>
                </div>
              </div>
            </MetadataRow>
          ) : null}
          {dataSource.deactivatedAt ? (
            <MetadataRow label={t("model.common.deactivatedAt")}>
              <span className="text-score-c">{formatDate(t, dataSource.deactivatedAt)}</span>
            </MetadataRow>
          ) : null}
        </dl>
      </section>

      <section className="space-y-2">
        <h3 className="text-14-bold text-fg-1">{t("model.propertySchema.modelNames")}</h3>
        <DataSourceSchemaSummary dataSource={dataSource} />
      </section>
    </div>
  );
}

export function DataSourceDetailPanel({ importProjectId, dataSourceId, mode = "peek" }: DataSourceDetailPanelProps) {
  const { t } = useTranslation();
  const { isReadOnly, projectId } = useDipLifecycle();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(
    `data-source:${importProjectId}:${dataSourceId}`,
  );
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const [openEditForm, setOpenEditForm] = useState(false);
  const [openEditSheetId, setOpenEditSheetId] = useState(false);
  const [openJsonEdit, setOpenJsonEdit] = useState(false);

  useEffect(() => {
    if (!isReadOnly) return;
    setOpenEditForm(false);
    setOpenEditSheetId(false);
    setOpenJsonEdit(false);
  }, [isReadOnly]);
  const { onActivate, onDeactivate } = useDataSourceActivation({
    importProjectId,
    canExecute: canExecuteMutation,
  });
  const { onDownloadClick, downloading } = useDataSourceJsonDownload();
  const { data, refetch } = useSuspenseQuery<GetDataSourceDetailQuery, GetDataSourceDetailQueryVariables>(
    GetDataSourceDetailDocument,
    {
      variables: { tenantId, dataSourceId },
    },
  );

  const dataSource = data.dataSources[0];
  const isAuthorized =
    projectId === importProjectId &&
    Boolean(dataSource?.dataSourceImportProjects.some(({ dataImportProjectId }) => dataImportProjectId === projectId));
  setAuthorized(isAuthorized);
  if (!dataSource || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  const detailRefetchQueries = [
    { query: GetDataSourceDetailDocument, variables: { tenantId, dataSourceId: dataSource.id } },
  ];
  const propertySchemaAssociations =
    dataSource.dataSourcePropertySchemaSet[0]?.propertySchemaSet.propertySchemaAssociations ?? [];

  return (
    <div className={cn("space-y-4", mode === "page" && "mx-auto max-w-[var(--content-max)] p-8")}>
      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={cn("text-fg-1", mode === "page" ? "text-dewey-title" : "text-[18px] font-semibold")}>
                {dataSource.name}
              </h2>
              {dataSource.deactivatedAt ? <Badge tone="status-draft">{t("ui.deactivated")}</Badge> : null}
            </div>
            <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-12-regular text-fg-3">
              <div className="flex gap-1.5">
                <dt>{t("model.common.id")}:</dt>
                <dd className="font-mono">{dataSource.id}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.createdAt")}:</dt>
                <dd>{formatDate(t, dataSource.createdAt)}</dd>
              </div>
              <div className="flex gap-1.5">
                <dt>{t("model.common.updatedAt")}:</dt>
                <dd>{formatDate(t, dataSource.updatedAt)}</dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            {dataSource.dataSourceGSheets ? (
              <>
                <Button asChild variant="outline" size="sm">
                  <a
                    href={DataSourceUtil.googleSpreadsheetUrl(
                      dataSource.dataSourceGSheets.googleSpreadsheetId,
                      dataSource.dataSourceGSheets.googleSheetId,
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("feature.dataImportProjects.detail.dataSource.linkText")}
                    <ExternalLink className="size-3.5" />
                  </a>
                </Button>
                <DropdownMenu>
                  <DipReadOnlyTooltip
                    disabled={isReadOnly}
                    ariaLabel={t("feature.dataImportProjects.detail.operation.sheetIdChange")}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        disabled={isReadOnly}
                        aria-label={t("feature.dataImportProjects.detail.operation.sheetIdChange")}
                      >
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                  </DipReadOnlyTooltip>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => {
                        if (!canExecuteMutation()) return;
                        setOpenEditSheetId(true);
                      }}
                    >
                      <Pencil className="size-3.5" />
                      {t("feature.dataImportProjects.detail.operation.sheetIdChange")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : null}
            {dataSource.dataSourceJson?.s3Path ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={downloading}
                onClick={() => onDownloadClick({ id: dataSource.id, name: dataSource.name })}
              >
                <Download className="size-3.5" />
                {t("feature.dataImportProjects.detail.dataSource.downloadJson")}
              </Button>
            ) : null}
            {dataSource.dataSourceJson?.s3Path ? (
              <DipReadOnlyTooltip
                disabled={isReadOnly}
                ariaLabel={t("feature.dataImportProjects.detail.dataSource.jsonEdit.editJson")}
              >
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isReadOnly}
                  onClick={() => {
                    if (!canExecuteMutation()) return;
                    setOpenJsonEdit(true);
                  }}
                >
                  <Pencil className="size-3.5" />
                  {t("feature.dataImportProjects.detail.dataSource.jsonEdit.editJson")}
                </Button>
              </DipReadOnlyTooltip>
            ) : null}
            <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.edit")}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isReadOnly}
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  setOpenEditForm(true);
                }}
              >
                <Pencil className="size-3.5" />
                {t("ui.edit")}
              </Button>
            </DipReadOnlyTooltip>
            {dataSource.deactivatedAt ? (
              <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.activate")}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isReadOnly}
                  onClick={() => {
                    if (!canExecuteMutation()) return;
                    onActivate(dataSource);
                  }}
                >
                  {t("ui.activate")}
                </Button>
              </DipReadOnlyTooltip>
            ) : (
              <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.deactivate")}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={isReadOnly}
                  onClick={() => {
                    if (!canExecuteMutation()) return;
                    onDeactivate(dataSource);
                  }}
                >
                  {t("ui.deactivate")}
                </Button>
              </DipReadOnlyTooltip>
            )}
          </div>
        </div>
      </section>

      <DataSourceInfoPanel
        dataSource={dataSource}
        onRefetch={() => void refetch()}
        readOnly={isReadOnly}
        canExecute={canExecuteMutation}
      />

      {!isReadOnly ? (
        <DataSourceEditBridgeDialog
          open={openEditForm}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setOpenEditForm(open);
          }}
          dataSourceId={dataSource.id}
          refetchQueriesOnEdit={detailRefetchQueries}
        />
      ) : null}
      {dataSource.dataSourceGSheets ? (
        <DataSourceSheetIdEditDialog
          open={openEditSheetId && !isReadOnly}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setOpenEditSheetId(open);
          }}
          dataSourceId={dataSource.id}
          sheetId={dataSource.dataSourceGSheets.googleSheetId}
          refetchQueriesOnEdit={detailRefetchQueries}
          readOnly={isReadOnly}
        />
      ) : null}
      <DataSourceJsonEditDialog
        open={openJsonEdit && !isReadOnly}
        onOpenChange={(open) => {
          if (!open || canExecuteMutation()) setOpenJsonEdit(open);
        }}
        dataSource={{ id: dataSource.id, name: dataSource.name }}
        schemaColumns={propertySchemaAssociations.map((a) => ({
          name: a.name,
          propertyTypeId: a.propertySchema.propertyTypeId,
        }))}
        onSaved={() => void refetch()}
        readOnly={isReadOnly}
      />
    </div>
  );
}
